/**
 * W65 — OpportunityActivityLog
 * Read-only list of last 10 activity events for an opportunity.
 */

const EVENT_KIND_LABELS: Record<string, string> = {
  line_added:      "Line added",
  line_changed:    "Line updated",
  line_removed:    "Line removed",
  override_set:    "Forecast override set",
  override_cleared:"Override cleared",
  owner_changed:   "Owner changed",
  status_changed:  "Status changed",
  credit_changed:  "Credit split updated",
};

const EVENT_KIND_COLORS: Record<string, string> = {
  line_added:      "#16A34A",
  line_changed:    "#EAB308",
  line_removed:    "#DC2626",
  override_set:    "#EA580C",
  override_cleared:"#6B7280",
  owner_changed:   "#0B0B0F",
  status_changed:  "#0B0B0F",
  credit_changed:  "#EA580C",
};

interface ActivityEvent {
  id: string;
  event_kind: string;
  event_detail?: Record<string, unknown>;
  actor_name?: string | null;
  created_at: string;
}

interface OpportunityActivityLogProps {
  events: ActivityEvent[];
}

function relTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  const hours = Math.floor(diff / 3_600_000);
  const days = Math.floor(diff / 86_400_000);
  if (mins < 2) return "just now";
  if (mins < 60) return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function eventSummary(kind: string, detail: Record<string, unknown> = {}): string {
  switch (kind) {
    case "line_added":
      return detail.description ? `"${detail.description}"` : "";
    case "line_changed":
      return "";
    case "line_removed":
      return "";
    case "override_set":
      return detail.override ? `→ ${detail.override}` : "";
    case "status_changed":
      return detail.action ? String(detail.action) : detail.status ? String(detail.status) : "";
    case "owner_changed":
      return "";
    case "credit_changed":
      return detail.credit_kind ? `${detail.credit_kind}: ${detail.credit_pct}%` : "";
    default:
      return "";
  }
}

export function OpportunityActivityLog({ events }: OpportunityActivityLogProps) {
  return (
    <div
      style={{
        background: "#FFFFFF",
        borderRadius: 8,
        border: "1px solid #E5E5E7",
        overflow: "hidden",
      }}
    >
      <div style={{ padding: "12px 16px 8px", borderBottom: "1px solid #E5E5E7" }}>
        <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: "#0B0B0F" }}>Activity Log</h3>
      </div>

      <div style={{ padding: "4px 0" }}>
        {events.length === 0 && (
          <div style={{ fontSize: 12, color: "#8E8E94", padding: "10px 16px" }}>No activity yet.</div>
        )}
        {events.map((ev) => {
          const color = EVENT_KIND_COLORS[ev.event_kind] ?? "#6B7280";
          const label = EVENT_KIND_LABELS[ev.event_kind] ?? ev.event_kind;
          const summary = eventSummary(ev.event_kind, ev.event_detail ?? {});
          return (
            <div
              key={ev.id}
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 10,
                padding: "8px 16px",
                borderBottom: "1px solid #EFEFEE",
              }}
            >
              {/* Color dot */}
              <div
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  background: color,
                  marginTop: 5,
                  flexShrink: 0,
                }}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: "#2A2A2E" }}>
                  {label}
                  {summary && (
                    <span style={{ fontSize: 11, fontWeight: 400, color: "#6B7280", marginLeft: 6 }}>
                      {summary}
                    </span>
                  )}
                </div>
                {ev.actor_name && (
                  <div style={{ fontSize: 11, color: "#8E8E94" }}>{ev.actor_name}</div>
                )}
              </div>
              <div style={{ fontSize: 11, color: "#C9C9CC", whiteSpace: "nowrap", flexShrink: 0 }}>
                {relTime(ev.created_at)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
